import { useAccountStorage } from '../../../../../../AppClients';
import { isLoginEmailExistInContactInformation } from '../../../../../../backend/features/accounts/shared';
import { isErrorEmailNotUnique } from '../../../../../../ui/features/accounts/utils';
import { useUserState } from '../../../../../../ui/hooks';
import { useContactManagementTranslation } from '../../../../../../ui/hooks/useContactManagementTranslation';
import { FullScreenWizard } from '@juvare/common-form-ui/FullScreenWizard';
import { showConfirmDialog } from '@juvare/common-web-ui/DialogHelper';
import { NavigationContext } from '@juvare/common-web-ui/NavigationContext';
import { showInfoSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import React from 'react';
import { useLocation, useNavigate } from 'react-router';
import publicRegistrationImage from '../../../../../assets/publicRegistrationImage.svg';
import { useAccountSections } from '../../../hooks/useAccountSections';
import { AccountSectionComponent } from '../../AccountSectionComponent/AccountSectionComponent';
export const AccountCreationWizard = () => {
    var _a, _b;
    const { t } = useContactManagementTranslation();
    const location = useLocation();
    const navigationContext = React.useContext(NavigationContext);
    const userState = useUserState();
    const navigate = useNavigate();
    const storageClient = useAccountStorage();
    const [changed, setChanged] = React.useState(false);
    const steps = ((_a = useAccountSections('new').data) !== null && _a !== void 0 ? _a : []).map((section) => {
        return {
            label: section.columns.title,
            render: (props) => React.createElement(AccountSectionComponent, Object.assign({ userState: userState, section: section, page: 'new' }, props)),
        };
    });
    const handleChanged = React.useCallback((changed) => {
        if (changed) {
            navigationContext.setChanged(t('common.confirmWizardClose'));
        }
        else {
            navigationContext.clearChanged();
        }
        setChanged(changed);
    }, [t, navigationContext]);
    const handleBack = React.useCallback(() => {
        if (changed) {
            showConfirmDialog({
                title: t('common.confirmWizardClose'),
                confirmLabel: t('common.closeWizardWithoutSaving'),
                onConfirm: () => {
                    var _a;
                    navigationContext.clearChanged();
                    if ((_a = location.state) === null || _a === void 0 ? void 0 : _a.back) {
                        navigate(-1);
                    }
                    else {
                        navigate('..');
                    }
                },
            });
        }
        else {
            navigationContext.navigate(() => { var _a; return (((_a = location.state) === null || _a === void 0 ? void 0 : _a.back) ? navigate(-1) : navigate('..')); });
        }
    }, [t, navigationContext, navigate, changed, (_b = location.state) === null || _b === void 0 ? void 0 : _b.back]);
    //TODO create a separate interface to handle organization parameters when organizationsEnabled setting is disabled. Related Ticket: CNEO-1683
    const handleSubmit = React.useCallback((data) => {
        var _a, _b;
        const accountData = Object.assign(Object.assign({}, data), { consent: false, firstName: (_a = data.firstName) !== null && _a !== void 0 ? _a : '', lastName: (_b = data.lastName) !== null && _b !== void 0 ? _b : '' });
        const savePromise = storageClient
            .post({ action: 'create', data: accountData })
            .then((response) => {
            navigationContext.navigate(() => {
                navigate({
                    pathname: '..',
                    search: new URLSearchParams({ id: response.id }).toString(),
                });
            });
        })
            .catch((error) => {
            var _a;
            if (isErrorEmailNotUnique(error)) {
                error.message = t('registration.userAlreadyExists');
                if (isLoginEmailExistInContactInformation(data) && userState.useLoginEmailInContactInformation) {
                    (_a = data.contactEmails) === null || _a === void 0 ? void 0 : _a.splice(data.contactEmails.length - 1, 1);
                }
            }
            throw error;
        });
        navigationContext.save(savePromise);
        return savePromise;
    }, [navigate, storageClient, navigationContext, t, userState.useLoginEmailInContactInformation]);
    const handleSubmitSuccess = React.useCallback(() => {
        showInfoSnackbar({ message: t('common.saved'), autoHideDuration: 2000 });
    }, [t]);
    if (steps && steps.length > 0) {
        return (React.createElement(FullScreenWizard, { open: true, steps: steps, title: t('forms.accountCreation.title'), image: { src: publicRegistrationImage, alt: 'Account Creation' }, onChanged: handleChanged, onCancel: handleBack, onSubmit: handleSubmit, onSubmitSuccess: handleSubmitSuccess }));
    }
    else {
        return null;
    }
};
